// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import type {
  DocumentWithRelations,
  Document,
  File,
  Notarization,
  NotarizationPostRequest,
  DocumentFilePostRequest,
  DocumentTemplateType,
} from "../../../api/src/types";
import { questionnaireApi } from "./questionnaire";
import { usersApi } from "./users";
import type { Section, Clause, User } from "../../../api/src/types";
import type { DocumentHistoryResponse } from "../../../api/src/documents/history";
import type {
  ApprovalGrantSuccessfulResponse,
  ApprovalRescindSuccessfulResponse,
} from "../../../api/src/documents/approval";
import type { CreateNotarizationProofResponse } from "../../../api/src/documents/notarizations/proof/route";
import type { GetRegenerationStatusResponse } from "../../../api/src/documents/regeneration-status";
import type {
  AnalyzeLlmResponse,
  DocumentTransferPostResponse,
  GetLatestLlmRunResponse,
  MarkReviewedSuccessfulResponse,
  ResetGenerationEligibleResponse,
  ResetGenerationResponse,
} from "../../../api/src/documents/route";
// Imported from the sub-router (not the parent re-export) so the
// Hono-inferred response type doesn't degrade to `any` on cross-
// package import — see the comment in `documents/route.ts` near the
// `document.route("/.../generated-pdf-url", ...)` mount.
import type { GeneratedPdfUrlResponse } from "../../../api/src/documents/generated-pdf-url";
import type {
  ClearGenerationTimestampsResponse,
  ClearApprovalsAndPDFsResponse,
} from "../../../api/src/admin/users/documents/route";
import type { DocumentNotarizationsGetResponse } from "../../../api/src/documents/notarizations/route";
import type { UserInvitesGetResponse } from "../../../api/src/users/route";

/**
 * Parses a filename out of a `Content-Disposition` header value.
 * Handles both quoted and unquoted forms. Returns `null` when the
 * header is missing or the filename token can't be extracted; callers
 * should fall back to a default name.
 */
export function parseFilenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(header);
  if (!match?.[1]) return null;
  return match[1].replace(/['"]/g, "");
}

export const documentsApi = createApi({
  reducerPath: "documentsApi",
  baseQuery,
  tagTypes: [
    "documents",
    "files",
    "notarizations",
    "document-modified",
    "invites",
    "document-history",
    "regeneration-status",
    "llm-runs",
    "AdminUsers",
  ],
  endpoints: (builder) => ({
    createDocument: builder.mutation<Document, Partial<Document>>({
      query: (document) => ({
        url: `documents`,
        method: "POST",
        body: document,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            questionnaireApi.util.invalidateTags([
              "QuestionnaireSections",
              "QuestionGroup",
            ]),
          );
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents"],
    }),
    getDocument: builder.query<DocumentWithRelations, string>({
      query: (id) => ({ url: `documents/${id}` }),
      providesTags: ["documents"],
    }),
    getDocumentsByUserId: builder.query<DocumentWithRelations[], string>({
      query: (userId) => ({
        url: `users/${userId}/documents`,
      }),
      providesTags: ["documents"],
    }),
    updateDocument: builder.mutation<
      Document,
      {
        stateId?: string;
        documentId: string;
        reviewedAt?: Date | null;
        finalizedAt?: Date | undefined;
        lastGeneratedInputHash?: string | null;
        lastGeneratedAt?: Date | null;
      }
    >({
      query: ({
        stateId,
        documentId,
        reviewedAt,
        finalizedAt,
        lastGeneratedInputHash,
        lastGeneratedAt,
      }) => ({
        url: `documents/${documentId}`,
        method: "PATCH",
        body: {
          stateId,
          reviewedAt,
          finalizedAt,
          lastGeneratedInputHash,
          lastGeneratedAt,
        },
      }),
      async onQueryStarted({ stateId }, { dispatch, queryFulfilled }) {
        try {
          if (stateId) {
            await queryFulfilled;
            dispatch(
              questionnaireApi.util.invalidateTags([
                "QuestionnaireSections",
                "QuestionGroup",
              ]),
            );
          }
        } catch (e) {
          console.error(e);
        }
      },
      // `notarizations` is included so a 409 `NOTARIZATION_EXISTS`
      // envelope from the `MARK_REVIEWED` / `MARK_FINALIZED` gate
      // propagates to the in-progress UI transition without forcing the
      // user to refresh. RTKQ array-form `invalidatesTags` runs on both
      // fulfilled and rejected, so this fires regardless of outcome.
      invalidatesTags: ["documents", "regeneration-status", "notarizations"],
    }),

    grantApproval: builder.mutation<
      ApprovalGrantSuccessfulResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/approval/grant`,
        method: "POST",
      }),
      // See `updateDocument` for the `notarizations` rationale — same
      // applies to the `ATTORNEY_GRANT_APPROVAL` gate's 409.
      invalidatesTags: ["documents", "notarizations"],
    }),
    // Customer "Approve & continue" on `/documents/review`. Dedicated
    // route (`POST /documents/:id/mark-reviewed`) so the single source of truth
    // `MARK_REVIEWED` gate always runs even when the column write is a
    // no-op — the prior client-side conditional in
    // `DocumentsReviewPage.finalizeAndNavigate` bypassed the gate for
    // already-reviewed-not-regenerated docs, letting stale tabs slip
    // past the in-progress-notarization check. The admin
    // "Update Finalized Date" popup still uses `updateDocument` for
    // explicit literal overrides (see `pages/admin/users/Notarization.tsx`).
    markReviewed: builder.mutation<
      MarkReviewedSuccessfulResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/mark-reviewed`,
        method: "POST",
      }),
      // Same tag set as `updateDocument` so stale-tab 409s
      // (NOTARIZATION_EXISTS, etc.) propagate to every dependent
      // surface without a manual refresh.
      invalidatesTags: ["documents", "regeneration-status", "notarizations"],
    }),
    rescindApproval: builder.mutation<
      ApprovalRescindSuccessfulResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/approval/rescind`,
        method: "POST",
      }),
      invalidatesTags: ["documents"],
    }),
    generateDocument: builder.mutation<Document, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/generate`,
        method: "POST",
      }),
      // See `updateDocument` for the `notarizations` rationale — same
      // applies to the `GENERATE` gate's 409.
      invalidatesTags: [
        "document-modified",
        "documents",
        "regeneration-status",
        "notarizations",
      ],
    }),
    createDocumentFile: builder.mutation<
      File,
      { documentId: string; file: DocumentFilePostRequest }
    >({
      query: ({ documentId, file }) => ({
        url: `documents/${documentId}/files`,
        method: "POST",
        body: file,
      }),
      async onQueryStarted({ file }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // If this is a notarization file, invalidate admin users data
          if (file.notarizationId) {
            dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
          }
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: (_, __, { file }) => {
        const tags: ("files" | "notarizations")[] = ["files"];
        // If this is a notarization file, also invalidate notarizations
        if (file.notarizationId) {
          tags.push("notarizations");
        }
        return tags;
      },
    }),
    getFilesByDocumentId: builder.query<File[], string>({
      query: (documentId) => ({ url: `documents/${documentId}/files` }),
      providesTags: ["files"],
    }),
    deleteFileByDocumentId: builder.mutation<
      void,
      { fileId: string; documentId: string }
    >({
      query: ({ fileId, documentId }) => ({
        url: `documents/${documentId}/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["files", "documents"],
    }),
    getNotarizationsByDocumentId: builder.query<
      DocumentNotarizationsGetResponse,
      string
    >({
      query: (documentId) => ({ url: `documents/${documentId}/notarizations` }),
      providesTags: ["notarizations"],
    }),
    createNotarization: builder.mutation<
      Notarization,
      { documentId: string; notarization: NotarizationPostRequest }
    >({
      query: ({ documentId, notarization }) => ({
        url: `documents/${documentId}/notarizations`,
        method: "POST",
        body: notarization,
      }),
      invalidatesTags: ["notarizations"],
    }),
    /**
     * Canonical customer/admin PDF read path. Returns a signed S3 URL
     * to the document's cached `generatedPdfFileId`, lazily generating
     * + caching on miss. Errors flow through as `FetchBaseQueryError`
     * so `useDocumentBlockerErrorHandler` parses the unified
     * `DOCUMENT_BLOCKED` 409 envelope natively. The editor preview
     * tab does NOT use this — it hits `/generate-pdf` directly.
     */
    generateCachedPdfUrl: builder.mutation<
      GeneratedPdfUrlResponse,
      {
        documentId: string;
        /**
         * Set to `"attachment"` when the caller intends a download
         * trigger (vs. an inline iframe preview). The server then
         * signs the URL with a `Content-Disposition: attachment;
         * filename="..."` so the browser downloads with the proper
         * customer-facing filename. Omit for inline preview.
         */
        disposition?: "attachment";
      }
    >({
      query: ({ documentId, disposition }) => ({
        url: `documents/${documentId}/generated-pdf-url`,
        method: "POST",
        ...(disposition ? { params: { disposition } } : {}),
      }),
    }),
    /**
     * Customer/admin DOCX download path. Server returns the DOCX bytes
     * directly (no S3 cache yet — see `// TODO upload to S3` in
     * `documents/route.ts:_generateDocxRoute`). The mutation's
     * `responseHandler` returns `{ blob, filename }` on success and
     * lets RTKQ parse the JSON body on error, so the unified 409
     * `DOCUMENT_BLOCKED` envelope arrives as a `FetchBaseQueryError`
     * that `useDocumentBlockerErrorHandler` parses natively. Callers
     * pass `blob` + `filename` to `downloadBlob` for the
     * `<a download>` trigger.
     */
    generateDocx: builder.mutation<
      { blob: Blob; filename: string },
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/generate-docx`,
        method: "POST",
        responseHandler: async (response): Promise<unknown> => {
          if (!response.ok) return (await response.json()) as unknown;
          const blob = await response.blob();
          const filename =
            parseFilenameFromContentDisposition(
              response.headers.get("content-disposition"),
            ) ?? `document-${documentId}.docx`;
          return { blob, filename };
        },
        // Don't let `responseHandler`'s non-Response return value
        // trip the default cache header inspection.
        cache: "no-cache",
      }),
    }),
    createNotarizationProof: builder.mutation<
      CreateNotarizationProofResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/notarizations/proof`,
        method: "POST",
        body: { documentId },
      }),
      invalidatesTags: ["notarizations"],
    }),
    deleteNotarization: builder.mutation<
      void,
      { documentId: string; notarizationId: string }
    >({
      query: ({ documentId, notarizationId }) => ({
        url: `documents/${documentId}/notarizations/${notarizationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["notarizations"],
    }),
    getInvitesByUserId: builder.query<
      UserInvitesGetResponse,
      { userId: string; templateType: string }
    >({
      query: ({ userId, templateType }) => ({
        url: `users/${userId}/documents/invites`,
        params: { templateType },
      }),
      providesTags: ["invites"],
    }),
    acceptInvite: builder.mutation<
      Document,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `users/${userId}/documents/${documentId}/accept-invite`,
        method: "GET",
      }),
      invalidatesTags: ["invites", "documents"],
    }),

    getDocumentHistoryCommits: builder.query<DocumentHistoryResponse, string>({
      query: (documentId) => ({ url: `documents/${documentId}/history` }),
      providesTags: ["document-history"],
    }),

    getDocumentCommitDetails: builder.query<
      {
        // todo: this should use the backend types, but because the editor uses DocumentWithRelations elsewhere, where the
        // dates are Dates even though they're really strings, it throws off the type checker here and isn't worth fixing currently
        current: { sections: Section[]; clauses: Clause[] };
        previous: { sections: Section[]; clauses: Clause[] };
        diffs: {
          id: string;
          type: "CREATE" | "MODIFY" | "DELETE";
          entity: "section" | "clause";
          moved?: true;
        }[];
      },
      { documentId: string; commitAt: string }
    >({
      query: ({ documentId, commitAt }) => ({
        url: `documents/${documentId}/history/single?commitAt=${encodeURIComponent(commitAt)}`,
      }),
      providesTags: ["document-history"],
    }),

    getDocumentCommitRangeDetails: builder.query<
      // todo: this should use the backend types, but because the editor uses DocumentWithRelations elsewhere, where the
      // dates are Dates even though they're really strings, it throws off the type checker here and isn't worth fixing currently
      {
        current: { sections: Section[]; clauses: Clause[] };
        previous: { sections: Section[]; clauses: Clause[] };
        diffs: {
          id: string;
          type: "CREATE" | "MODIFY" | "DELETE";
          entity: "section" | "clause";
          moved?: true;
        }[];
      },
      { documentId: string; startCommitAt: string; endCommitAt: string }
    >({
      query: ({ documentId, startCommitAt, endCommitAt }) => ({
        url: `documents/${documentId}/history/range?startCommitAt=${encodeURIComponent(startCommitAt)}&endCommitAt=${encodeURIComponent(endCommitAt)}`,
      }),
      providesTags: ["document-history"],
    }),

    resetNotarization: builder.mutation<void, { notarizationId: string }>({
      query: ({ notarizationId }) => ({
        url: `notarizations/${notarizationId}/reset`,
        method: "POST",
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["notarizations", "documents"],
    }),

    forceResetNotarization: builder.mutation<void, { notarizationId: string }>({
      query: ({ notarizationId }) => ({
        url: `notarizations/${notarizationId}/force-reset`,
        method: "PATCH",
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["notarizations", "documents"],
    }),

    // Admin-only: in non-production environments, force a notarization at the
    // notarization stage to "completed" without going through Proof's signing
    // flow. The document's existing proofFile is reused as the notarized file.
    // Mirrors the same side effects as the proof webhook's `transaction.completed`.
    bypassProofNotarization: builder.mutation<void, { notarizationId: string }>(
      {
        query: ({ notarizationId }) => ({
          url: `notarizations/${notarizationId}/admin-bypass-proof`,
          method: "PATCH",
        }),
        async onQueryStarted(_args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
          } catch (e) {
            console.error(e);
          }
        },
        invalidatesTags: ["notarizations", "documents"],
      },
    ),

    getDocumentParticipants: builder.query<
      DocumentWithRelations & {
        attorneyA?: {
          user: User;
          profilePhotoUrl: string | null;
        } | null;
        attorneyB?: {
          user: User;
          profilePhotoUrl: string | null;
        } | null;
      },
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/participants`,
        method: "GET",
      }),
      providesTags: ["documents"],
    }),
    getRegenerationStatus: builder.query<GetRegenerationStatusResponse, string>(
      {
        query: (documentId) => ({
          url: `documents/${documentId}/regeneration-status`,
        }),
        providesTags: ["regeneration-status"],
      },
    ),
    getResetGenerationEligible: builder.query<
      ResetGenerationEligibleResponse,
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/reset-generation-eligible`,
      }),
      providesTags: ["documents"],
    }),
    resetGeneration: builder.mutation<ResetGenerationResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/reset-generation`,
        method: "POST",
      }),
      invalidatesTags: ["documents", "regeneration-status", "AdminUsers"],
    }),
    clearGenerationTimestamps: builder.mutation<
      ClearGenerationTimestampsResponse,
      { documentId: string; count: number }
    >({
      query: ({ documentId, count }) => ({
        url: `admin/users/documents/${documentId}/clear-generation-timestamps`,
        method: "POST",
        body: { count },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents", "AdminUsers"],
    }),
    clearApprovalsAndPDFs: builder.mutation<
      ClearApprovalsAndPDFsResponse,
      { documentId: string }
    >({
      query: ({ documentId }) => ({
        url: `admin/users/documents/${documentId}/clear-approvals-and-pdfs`,
        method: "POST",
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents", "notarizations", "AdminUsers"],
    }),
    analyzeLlm: builder.mutation<AnalyzeLlmResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/analyze-llm`,
        method: "POST",
      }),
      invalidatesTags: ["documents", "llm-runs", "AdminUsers"],
    }),
    getLatestLlmRun: builder.query<GetLatestLlmRunResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/llm-runs/latest`,
      }),
      providesTags: ["llm-runs"],
    }),
    transferDocument: builder.mutation<
      DocumentTransferPostResponse,
      { documentId: string; userId: string; type: DocumentTemplateType }
    >({
      query: ({ documentId, userId, type }) => ({
        url: `documents/${documentId}/transfer`,
        method: "POST",
        body: { userId, type },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["documents"],
    }),
  }),
});

export const {
  useGetDocumentQuery,
  useGetDocumentsByUserIdQuery,
  useLazyGetDocumentsByUserIdQuery,
  useUpdateDocumentMutation,
  useGenerateDocumentMutation,
  useCreateDocumentMutation,
  useCreateDocumentFileMutation,
  useGrantApprovalMutation,
  useRescindApprovalMutation,
  useMarkReviewedMutation,
  useGetFilesByDocumentIdQuery,
  useDeleteFileByDocumentIdMutation,
  useGetNotarizationsByDocumentIdQuery,
  useCreateNotarizationMutation,
  useDeleteNotarizationMutation,
  useGenerateCachedPdfUrlMutation,
  useGenerateDocxMutation,
  useCreateNotarizationProofMutation,
  useGetInvitesByUserIdQuery,
  useAcceptInviteMutation,
  useGetDocumentHistoryCommitsQuery,
  useGetDocumentCommitDetailsQuery,
  useGetDocumentCommitRangeDetailsQuery,
  useResetNotarizationMutation,
  useForceResetNotarizationMutation,
  useBypassProofNotarizationMutation,
  useGetDocumentParticipantsQuery,
  useGetRegenerationStatusQuery,
  useGetResetGenerationEligibleQuery,
  useResetGenerationMutation,
  useClearGenerationTimestampsMutation,
  useClearApprovalsAndPDFsMutation,
  useAnalyzeLlmMutation,
  useGetLatestLlmRunQuery,
  useTransferDocumentMutation,
} = documentsApi;
